import { CustomState } from "@bevco/Store/Reducers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ResetShippingAddressFormValidation";
import validateShippingAddressForm from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ValidateShippingAddressForm";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    CustomerValidationDto,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@insite/content-library/Components/CustomerAddressFormFields";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import { TextFieldProps } from "@insite/mobius/TextField";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: ShippingAddressFormStyles;
}

const mapStateToProps = (state: CustomState) => {
    return {
        customNewShippingAddressForm: state.customNewShippingAddressForm,
        currentShipTo: getCurrentShipToState(state),
    };
};

const mapDispatchToProps = {
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Components/MyAccount/SetNewShippingAddress" as any,
            address,
        });
    },
    validateForm: makeHandlerChainAwaitable<Parameters<typeof validateShippingAddressForm>[0], boolean>(
        validateShippingAddressForm,
    ),
    resetShippingAddressFormValidation,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

type State = {
    styles: ShippingAddressFormStyles;
};

export interface ShippingAddressFormStyles {
    formFields?: CustomerAddressFormFieldStyles;
}

export const oneTimeAddressFormStyles: ShippingAddressFormStyles = {};

const styles = oneTimeAddressFormStyles;

class ShippingAddressForm extends React.Component<Props, State> {
    static contextType = CheckoutShippingFormContext;

    context!: React.ContextType<typeof CheckoutShippingFormContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            styles: mergeToNew(styles, this.props.extendedStyles),
        };
    }

    componentDidMount() {
        const { customNewShippingAddressForm, fieldDisplay, validateForm, currentShipTo } = this.props;
        if (!customNewShippingAddressForm || !currentShipTo.value) {
            return;
        }

        const { address } = customNewShippingAddressForm;
        const { validation } = currentShipTo.value;
        const customValidation = this.overrideValidation(validation);

        if (!validation || !customValidation) {
            return;
        }

        const shouldValidate =
            customValidation.firstName?.isDisabled === false ||
            customValidation.lastName?.isDisabled === false ||
            customValidation.companyName?.isDisabled === false ||
            customValidation.attention?.isDisabled === false ||
            customValidation.address1?.isDisabled === false ||
            customValidation.address2?.isDisabled === false ||
            customValidation.address3?.isDisabled === false ||
            customValidation.address4?.isDisabled === false ||
            customValidation.city?.isDisabled === false ||
            customValidation.state?.isDisabled === false ||
            customValidation.country?.isDisabled === false ||
            customValidation.postalCode?.isDisabled === false ||
            customValidation.phone?.isDisabled === false ||
            customValidation.email?.isDisabled === false ||
            customValidation.fax?.isDisabled === false;
        if (shouldValidate) {
            this.context.validators.shippingAddress = () =>
                validateForm({
                    address,
                    validation: customValidation,
                    fieldDisplay,
                });
            this.props.resetShippingAddressFormValidation();
        }
    }

    componentWillUnmount() {
        this.context.validators.shippingAddress = undefined;
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { customNewShippingAddressForm: prevCustomNewShippingAddressForm, fieldDisplay: prevFieldDisplay } =
            prevProps;
        const { customNewShippingAddressForm, fieldDisplay, validateForm, currentShipTo } = this.props;
        if (prevCustomNewShippingAddressForm !== customNewShippingAddressForm && customNewShippingAddressForm) {
            const { address } = customNewShippingAddressForm;
            const { validation } = currentShipTo.value || {};
            const customValidation = this.overrideValidation(validation);

            if (
                (!prevCustomNewShippingAddressForm ||
                    prevCustomNewShippingAddressForm.address !== address ||
                    prevCustomNewShippingAddressForm.address.validation !== validation ||
                    prevFieldDisplay !== fieldDisplay) &&
                customValidation
            ) {
                const shouldValidate =
                    customValidation.firstName?.isDisabled === false ||
                    customValidation.lastName?.isDisabled === false ||
                    customValidation.companyName?.isDisabled === false ||
                    customValidation.attention?.isDisabled === false ||
                    customValidation.address1?.isDisabled === false ||
                    customValidation.address2?.isDisabled === false ||
                    customValidation.address3?.isDisabled === false ||
                    customValidation.address4?.isDisabled === false ||
                    customValidation.city?.isDisabled === false ||
                    customValidation.state?.isDisabled === false ||
                    customValidation.country?.isDisabled === false ||
                    customValidation.postalCode?.isDisabled === false ||
                    customValidation.phone?.isDisabled === false ||
                    customValidation.email?.isDisabled === false ||
                    customValidation.fax?.isDisabled === false;
                if (shouldValidate) {
                    this.context.validators.shippingAddress = () =>
                        validateForm({
                            address,
                            validation: customValidation,
                            fieldDisplay,
                        });
                    // Don't auto-run validation here - parent component handles it
                } else {
                    this.context.validators.shippingAddress = undefined;
                }
            }
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { customNewShippingAddressForm, setAddress } = this.props;
        if (!customNewShippingAddressForm) {
            return;
        }
        const { address } = customNewShippingAddressForm;
        setAddress({
            ...address,
            [event.target.name]: event.target.value,
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const { customNewShippingAddressForm, setAddress } = this.props;
        if (!customNewShippingAddressForm) {
            return;
        }
        const { address } = customNewShippingAddressForm;
        setAddress({
            ...address,
            country,
        });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        const { customNewShippingAddressForm, setAddress } = this.props;
        if (!customNewShippingAddressForm) {
            return;
        }
        const { address } = customNewShippingAddressForm;
        setAddress({
            ...address,
            state,
        });
    };

    overrideValidation = (validation: any) => {
        if (validation) {
            const newData: { [key: string]: any } = {};

            for (const key in validation) {
                newData[key] = {
                    ...validation[key],
                    isDisabled: false,
                    isRequired: !(key === "address2" || key === "phone"),
                };
            }

            return newData as CustomerValidationDto;
        }
    };

    render() {
        const { customNewShippingAddressForm, countries, fieldDisplay, currentShipTo } = this.props;
        const customFieldsDisplay = {
            ...fieldDisplay,
            address3: {
                ...fieldDisplay.address3,
                isVisible: false,
            },
            address4: {
                ...fieldDisplay.address4,
                isVisible: false,
            },
            attention: {
                ...fieldDisplay.attention,
                isVisible: false,
            },
            fax: {
                ...fieldDisplay.fax,
                isVisible: false,
            },
        };
        const { styles } = this.state;
        if (!customNewShippingAddressForm || !currentShipTo.value) {
            return null;
        }
        const { address, formErrors } = customNewShippingAddressForm;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = address;

        const { validation } = currentShipTo.value;
        const customValidation = this.overrideValidation(validation);

        if (!validation || !customValidation) {
            return null;
        }

        return (
            <CustomerAddressFormFields
                firstName={firstName}
                lastName={lastName}
                attention={attention}
                companyName={companyName}
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                country={country}
                city={city}
                state={state}
                postalCode={postalCode}
                phone={phone}
                email={email}
                fax={fax}
                onChangeFirstName={this.handleInputChange}
                onChangeLastName={this.handleInputChange}
                onChangeAttention={this.handleInputChange}
                onChangeCompanyName={this.handleInputChange}
                onChangeAddress1={this.handleInputChange}
                onChangeAddress2={this.handleInputChange}
                onChangeAddress3={this.handleInputChange}
                onChangeAddress4={this.handleInputChange}
                onChangeCountry={this.handleCountryChange}
                onChangeState={this.handleStateChange}
                onChangeCity={this.handleInputChange}
                onChangePostalCode={this.handleInputChange}
                onChangePhone={this.handleInputChange}
                onChangeEmail={this.handleInputChange}
                onChangeFax={this.handleInputChange}
                firstNameError={formErrors.firstName}
                lastNameError={formErrors.lastName}
                attentionError={formErrors.attention}
                companyNameError={formErrors.companyName}
                address1Error={formErrors.address1}
                address2Error={formErrors.address2}
                address3Error={formErrors.address3}
                address4Error={formErrors.address4}
                countryError={formErrors.country}
                stateError={formErrors.state}
                cityError={formErrors.city}
                postalCodeError={formErrors.postalCode}
                phoneError={formErrors.phone}
                emailError={formErrors.email}
                faxError={formErrors.fax}
                countries={countries}
                validation={customValidation}
                fieldDisplay={customFieldsDisplay as any}
                extendedStyles={styles.formFields}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShippingAddressForm);
